import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquareText, Smartphone, FileImage, Users, TestTubeDiagonal, Send,
  Check, ArrowRight, Upload, Loader2, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * WhatsApp Broadcast page — marketing blasts via Twilio + approved templates.
 *
 * 5-step flow:
 *   1. "המכשירים שלי" — select which Twilio WhatsApp sender to use
 *   2. "הקמת תבנית" — create/select a content template (video/image/text)
 *   3. "חיבור מספרי טלפון" — paste numbers or import from CSV/Sheets
 *   4. "בדיקה" — send to a single test number first
 *   5. "שלח" — blast to everyone with 5s rate limit between sends
 */

const STEPS = [
  { num: 1, label: "המכשירים שלי", icon: Smartphone, color: "from-emerald-500 to-teal-600" },
  { num: 2, label: "תבנית הודעה", icon: FileImage, color: "from-blue-500 to-cyan-600" },
  { num: 3, label: "מספרי טלפון", icon: Users, color: "from-amber-500 to-orange-600" },
  { num: 4, label: "בדיקה", icon: TestTubeDiagonal, color: "from-violet-500 to-purple-600" },
  { num: 5, label: "שליחה", icon: Send, color: "from-rose-500 to-pink-600" },
];

export default function WABroadcast() {
  const [step, setStep] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [senderCompanyId, setSenderCompanyId] = useState("");
  const [templateType, setTemplateType] = useState("text"); // "text" | "image" | "video"
  const [templateBody, setTemplateBody] = useState("שלום {name},\n\nיש לנו עדכון חשוב בשבילך.\n\nלפרטים נוספים ניתן לבקר באתר שלנו.");
  const [mediaUrl, setMediaUrl] = useState("");
  const [numbersText, setNumbersText] = useState("");
  const [testNumber, setTestNumber] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const withWa = (data.companies || []).filter((c) => (c.channels || []).includes("whatsapp"));
        setCompanies(withWa);
        if (withWa[0]) setSenderCompanyId(withWa[0].id);
      } catch { /* ignore */ }
    })();
  }, []);

  const parsedNumbers = numbersText
    .split(/[\n,;]/)
    .map((n) => n.trim())
    .filter((n) => n.length > 5);

  const canAdvance = {
    1: !!senderCompanyId,
    2: !!templateBody && (templateType === "text" || !!mediaUrl),
    3: parsedNumbers.length > 0,
    4: true, // test is optional
  };

  const selectedCompany = companies.find((c) => c.id === senderCompanyId);

  const runTest = async () => {
    if (!testNumber) { toast.error("הזיני מספר בדיקה"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/broadcast/test", {
        company_id: senderCompanyId,
        to_number: testNumber,
        body: templateBody,
        media_url: templateType !== "text" ? mediaUrl : null,
      });
      if (data.ok) toast.success("הודעת הבדיקה נשלחה בהצלחה");
      else toast.error(data.error || "השליחה נכשלה");
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשליחת בדיקה");
    } finally {
      setSending(false);
    }
  };

  const runBroadcast = async () => {
    if (parsedNumbers.length === 0) return;
    if (!window.confirm(`לשלוח דיוור ל-${parsedNumbers.length} מספרים? לא ניתן לבטל אחרי תחילת השליחה.`)) return;
    setSending(true);
    setResults(null);
    try {
      const { data } = await api.post("/broadcast/send", {
        company_id: senderCompanyId,
        numbers: parsedNumbers,
        body: templateBody,
        media_url: templateType !== "text" ? mediaUrl : null,
      });
      setResults(data);
      toast.success(`${data.sent}/${data.total} נשלחו בהצלחה`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשליחת הדיוור");
    } finally {
      setSending(false);
    }
  };

  return (
    <div dir="rtl" className="p-6 sm:p-10 max-w-5xl mx-auto" data-testid="wa-broadcast">
      <Toaster position="top-center" richColors />

      {/* Breadcrumb */}
      <Link to="/S.ADMIN/automations" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900 mb-4" data-testid="back-to-hub">
        <ArrowRight className="h-3 w-3" />
        חזרה לאוטומציות
      </Link>

      {/* Header */}
      <div className="mb-8 flex items-start gap-3">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
          <MessageSquareText className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-stone-900">
            דיוור הודעות WhatsApp
          </h1>
          <p className="text-stone-600 mt-1 text-sm">
            5 צעדים קצרים לשליחת קמפיין מקצועי לכל הלקוחות שלך.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-6 shadow-sm" data-testid="stepper">
        <div className="flex items-center justify-between gap-2 overflow-x-auto">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isDone = step > s.num;
            const isCurrent = step === s.num;
            return (
              <div key={s.num} className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setStep(s.num)}
                  disabled={!isDone && !isCurrent}
                  className="flex flex-col items-center gap-1.5 disabled:opacity-50"
                  data-testid={`step-${s.num}`}
                >
                  <div className={[
                    "h-10 w-10 rounded-xl flex items-center justify-center font-bold text-sm shadow-md transition-all",
                    isDone ? `bg-gradient-to-br ${s.color} text-white` :
                    isCurrent ? `bg-gradient-to-br ${s.color} text-white scale-110` :
                    "bg-stone-100 text-stone-400"
                  ].join(" ")}>
                    {isDone ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className={`text-[10px] font-semibold ${isCurrent ? "text-stone-900" : "text-stone-500"}`}>
                    {s.num}. {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 w-8 lg:w-16 ${isDone ? "bg-emerald-400" : "bg-stone-200"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step panel */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm min-h-[300px]" data-testid="step-panel">
        {step === 1 && (
          <div className="space-y-4" data-testid="step-1-content">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-bold">מאיזה מכשיר לשלוח?</h2>
            </div>
            <p className="text-sm text-stone-600">בחרי איזה חיבור WhatsApp (חברה) ישלח את הדיוור. זה יופיע בפרופיל שהלקוח רואה.</p>
            {companies.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                אין עדיין חברה עם ערוץ WhatsApp. קודם הקימי חברה עם Twilio בהגדרות.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {companies.map((c) => (
                  <button type="button"
                    key={c.id}
                    onClick={() => setSenderCompanyId(c.id)}
                    className={[
                      "text-right p-4 rounded-xl border-2 transition-all",
                      senderCompanyId === c.id
                        ? "border-emerald-500 bg-emerald-50 shadow-md"
                        : "border-stone-200 bg-white hover:border-stone-300",
                    ].join(" ")}
                    data-testid={`sender-${c.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center font-bold">
                        {c.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-sm">{c.name}</p>
                        <p className="text-[11px] text-stone-500" dir="ltr">
                          {c.manychat_api_key ? "ManyChat" : (c.dialog360_phone_number || c.twilio_whatsapp_from || "—")}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="step-2-content">
            <div className="flex items-center gap-2">
              <FileImage className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold">תבנית ההודעה</h2>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "text", label: "טקסט בלבד", icon: "📝" },
                { key: "image", label: "תמונה + טקסט", icon: "🖼️" },
                { key: "video", label: "סרטון + טקסט", icon: "🎬" },
              ].map((t) => (
                <button type="button"
                  key={t.key}
                  onClick={() => setTemplateType(t.key)}
                  className={[
                    "p-3 rounded-xl border-2 text-center transition",
                    templateType === t.key ? "border-blue-500 bg-blue-50" : "border-stone-200 hover:border-stone-300",
                  ].join(" ")}
                  data-testid={`template-type-${t.key}`}
                >
                  <div className="text-2xl mb-1">{t.icon}</div>
                  <p className="text-xs font-bold">{t.label}</p>
                </button>
              ))}
            </div>
            {templateType !== "text" && (
              <div>
                <Label>קישור ל-{templateType === "image" ? "תמונה (JPG/PNG)" : "סרטון (MP4)"}</Label>
                <Input
                  dir="ltr"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder="https://example.com/media.jpg"
                  className="mt-1 text-right"
                  data-testid="media-url-input"
                />
                <p className="text-[11px] text-stone-500 mt-1">העלי את הקובץ לכל שרת ציבורי (Google Drive/Dropbox/Cloudinary) והדביקי את הקישור הישיר כאן.</p>
              </div>
            )}
            <div>
              <Label>תוכן ההודעה</Label>
              <Textarea
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                rows={6}
                className="mt-1 font-mono text-sm"
                data-testid="template-body-input"
              />
              <p className="text-[11px] text-stone-500 mt-1">
                אפשר להשתמש במשתנה <code className="bg-stone-100 px-1 rounded">{"{name}"}</code> והוא יוחלף בשם הלקוח.
              </p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-3-content">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-bold">לאיזה מספרים לשלוח?</h2>
            </div>
            <Label>הדביקי מספרים (שורה אחת לכל מספר או מופרדים בפסיק)</Label>
            <Textarea
              dir="ltr"
              value={numbersText}
              onChange={(e) => setNumbersText(e.target.value)}
              rows={10}
              className="font-mono text-xs text-right"
              placeholder="0504944192&#10;0521234567&#10;+972501234567"
              data-testid="numbers-textarea"
            />
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-stone-900">{parsedNumbers.length} מספרים תקפים</span>
              <Button variant="outline" size="sm" className="mr-auto" data-testid="import-csv-btn">
                <Upload className="h-3.5 w-3.5 ml-1" />
                ייבוא מ-CSV (בקרוב)
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="step-4-content">
            <div className="flex items-center gap-2">
              <TestTubeDiagonal className="h-5 w-5 text-violet-600" />
              <h2 className="text-lg font-bold">בדיקה קצרה לפני השליחה</h2>
            </div>
            <p className="text-sm text-stone-600">שלחי הודעת בדיקה למספר אחד כדי לוודא שהכל נראה כמו שצריך.</p>
            <div>
              <Label>מספר בדיקה (רצוי המספר שלך)</Label>
              <Input
                dir="ltr"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="0504944192"
                className="mt-1 text-right font-mono"
                data-testid="test-number-input"
              />
            </div>
            <Button
              onClick={runTest}
              disabled={sending || !testNumber}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="run-test-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><TestTubeDiagonal className="h-4 w-4 ml-2" />שלחי בדיקה</>}
            </Button>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4" data-testid="step-5-content">
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-rose-600" />
              <h2 className="text-lg font-bold">שליחה לכל הרשימה</h2>
            </div>
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">שולח</span>
                <span className="font-bold">{selectedCompany?.name || "—"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">סוג תבנית</span>
                <span className="font-bold">{templateType === "text" ? "טקסט" : templateType === "image" ? "תמונה + טקסט" : "סרטון + טקסט"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">נמענים</span>
                <span className="font-bold text-rose-700">{parsedNumbers.length} מספרים</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">זמן משוער</span>
                <span className="font-bold">{Math.ceil((parsedNumbers.length * 5) / 60)} דקות</span>
              </div>
            </div>
            <Button
              onClick={runBroadcast}
              disabled={sending || parsedNumbers.length === 0}
              className="w-full bg-gradient-to-l from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white h-12 text-base font-bold"
              data-testid="run-broadcast-btn"
            >
              {sending ? <><Loader2 className="h-5 w-5 animate-spin ml-2" />שולח...</> : <><Sparkles className="h-5 w-5 ml-2" />שליחה לכולם</>}
            </Button>
            {results && (
              <div className="space-y-2 pt-2" data-testid="broadcast-results">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{results.sent}</p>
                    <p className="text-[10px] text-green-700">נשלחו</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{results.failed}</p>
                    <p className="text-[10px] text-red-700">נכשלו</p>
                  </div>
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-stone-700">{results.total}</p>
                    <p className="text-[10px] text-stone-700">סה״כ</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav buttons */}
      <div className="flex items-center justify-between mt-6 gap-2">
        <Button
          variant="ghost"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          data-testid="prev-step"
        >
          חזרה
        </Button>
        {step < 5 && (
          <Button
            onClick={() => setStep((s) => Math.min(5, s + 1))}
            disabled={!canAdvance[step]}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid="next-step"
          >
            הבא
          </Button>
        )}
      </div>
    </div>
  );
}
